import { ShippingTimePromoInfoBox } from '@/components/molecules/purchasePromoInfoBox'
import { Flex } from '@/components/atoms/Grid'
import { Tiny2 } from '@/components/atoms/typography'
import styled from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import { validFor } from '@/theme/validFor'
import { FilterQueryItem } from 'types/filterQueryItem'
import {
  useActiveFilterDispatch,
  useFilter
} from '@/providers/filterProvider/filtersProvider'
import ToggleCheckBoxRedesign from '@/components/molecules/toggleCheckBoxRedesign'

type InStorageCheckboxProps = {
  filterType?:
    | FilterQueryItem.STOCK
    | FilterQueryItem.MAIN_WAREHOUSE
    | FilterQueryItem.IN_SALE
    | FilterQueryItem.NEWS
}

const setFilterPayload = (
  type: InStorageCheckboxProps['filterType'],
  isActive: boolean
) => {
  const filterPayload = {
    [FilterQueryItem.STOCK]: {
      id: isActive,
      name: undefined,
      getParamType: FilterQueryItem.STOCK
    },
    [FilterQueryItem.MAIN_WAREHOUSE]: {
      id: isActive,
      name: undefined,
      getParamType: FilterQueryItem.MAIN_WAREHOUSE
    },
    [FilterQueryItem.IN_SALE]: {
      id: isActive,
      name: undefined,
      getParamType: FilterQueryItem.IN_SALE
    },
    [FilterQueryItem.NEWS]: {
      id: isActive,
      name: undefined,
      getParamType: FilterQueryItem.NEWS
    }
  }
  return filterPayload[type]
}

const setFilterDescription = (type: InStorageCheckboxProps['filterType']) => {
  const { t } = useTranslation('common')
  const filterDescription = {
    [FilterQueryItem.STOCK]: {
      title: t('Category.Filter.inStorage'),
      promoTitle: null
    },
    [FilterQueryItem.MAIN_WAREHOUSE]: {
      title: t('Category.Filter.inMainWarehouse'),
      promoTitle: null
    },
    [FilterQueryItem.IN_SALE]: {
      title: t('Category.Filter.inSale'),
      promoTitle: null
    },
    [FilterQueryItem.NEWS]: {
      title: t('Category.Filter.news'),
      promoTitle: null
    }
  }
  return filterDescription[type]
}

export const FilterCheckboxRedesign = ({
  filterType,
  ...rest
}: InStorageCheckboxProps & Rest): JSX.Element => {
  const { activeFilters } = useFilter()
  const { toggleFilterAndSetUrl } = useActiveFilterDispatch()
  const {
    id: filterId,
    name: filterName,
    getParamType: filterParamType
  } = setFilterPayload(filterType, activeFilters[filterType]) || {}
  const { title: filterTitle, promoTitle: filterPromoTitle } =
    setFilterDescription(filterType) || {}
  const onCheckboxClick = () =>
    toggleFilterAndSetUrl(filterId, filterName, filterParamType)

  return (
    <Wrapper
      data-testid={`filter-${filterType}`}
      {...rest}
      isSale={filterType === FilterQueryItem.IN_SALE}
    >
      <ToggleCheckBoxRedesign
        isChecked={activeFilters[filterType]}
        onCheckboxClick={onCheckboxClick}
      >
        <StyledFlex>
          <StyledTiny2>{filterTitle}</StyledTiny2>
        </StyledFlex>
      </ToggleCheckBoxRedesign>
      {filterPromoTitle ? (
        <ShippingTimePromoInfoBox
          ml="6px"
          display={{ tablet: 'flex', desktop: 'flex', mobile: 'none' }}
          shippingTitle={filterPromoTitle}
        />
      ) : null}
    </Wrapper>
  )
}

const Wrapper = styled(Flex)`
  border: solid 0.5px ${(props) => props.theme.colors.buttonBorderNew};
  border-radius: 2px;
  justify-content: space-between;
  align-self: center;
  color: ${(props) => props.theme.colors.onBackground};
  padding: 5px 8px;
  box-sizing: border-box;
  height: 33px;
  ${validFor.desktop`
    padding: 8px 12px;
  `};
  ${validFor.mobile`
    border: none;
    border-bottom: solid 0.5px ${(props) => props.theme.colors.navMenuGrey};
    padding: 27px 12px;
  `};
  ${validFor.tablet`
    border: none;
    border-bottom: solid 0.5px ${(props) => props.theme.colors.navMenuGrey};
    padding: 27px 12px;
  `};
`

const StyledTiny2 = styled(Tiny2)`
  margin: 0;
  font-weight: 400;
  letter-spacing: -1%;
  line-height: 1;
  font-size: 14px;
  ${validFor.mobile`
    font-weight: 500;
    font-size: 14px;
  `};
  ${validFor.tablet`
    font-weight: 500;
    font-size: 14px;
  `};
`

const StyledFlex = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex: 1;
`
